import { Link } from "react-router-dom";
import { useState, useEffect } from "react";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import hero from "../assets/yb (16).jpg";
import safariJeep from "../assets/yb (15).jpg";
import { publicFetch, API_BASE_URL, apiEndpoints } from "../config/api";

const packages = [
  {
    name: "Signature Leopard Quest",
    description:
      "Half-day exploration of Yala Block I with sunrise tracking and curated photo stops.",
    duration: "4 hours · Sunrise departure",
    link: "/booking?package=leopard",
  },
  {
    name: "Full-Day Wilderness Immersion",
    description:
      "Private jeep, gourmet bush lunch, and expert naturalist guiding through multiple habitats.",
    duration: "10 hours · All-inclusive",
    link: "/booking?package=immersion",
  },
  {
    name: "Bundala Birding Retreat",
    description:
      "Specialist-led experience focused on migratory birds, lagoons, and golden-hour photography.",
    duration: "5 hours · Afternoon departure",
    link: "/booking?package=birding",
  },
];

export default function Home() {
  const [reviews, setReviews] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Helper function to get full image URL
  const getImageUrl = (imagePath) => {
    if (!imagePath) return "";
    // If already a full URL, return as is
    if (imagePath.startsWith("http://") || imagePath.startsWith("https://")) {
      return imagePath;
    }
    // Backend may return full server path (e.g. /root/.../uploads/reviews/file.jpg) — use web path from "uploads" onwards
    const uploadsIndex = imagePath.indexOf("uploads");
    if (uploadsIndex !== -1) {
      imagePath = "/" + imagePath.slice(uploadsIndex);
    }
    return `${API_BASE_URL}${imagePath}`;
  };

  // Fetch published reviews
  useEffect(() => {
    const fetchReviews = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await publicFetch(apiEndpoints.reviews.published);

        if (!response.ok) {
          throw new Error("Failed to fetch reviews");
        }

        const result = await response.json();

        if (result.success) {
          setReviews(result.data || []);
          setStats(result.stats || null);
        } else {
          throw new Error(result.message || "Failed to fetch reviews");
        }
      } catch (err) {
        console.error("Error fetching reviews:", err);
        setError(err.message);
        // Set empty arrays on error to prevent crashes
        setReviews([]);
        setStats(null);
      } finally {
        setLoading(false);
      }
    };

    fetchReviews();
  }, []);
  return (
    <main className="bg-gradient-to-b from-[#1a1f28] via-[#202834] to-[#0a0d12] text-white">
      {/* Hero Section */}
      <section className="relative flex min-h-[90vh] items-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            className="h-full w-full object-cover object-center"
            src={hero}
            alt="Wildlife in Yala National Park"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0b121a]/10 via-[#101821]/92 to-[#1d232e]" />
        </div>
        <div className="relative mx-auto flex w-full max-w-7xl flex-col items-center gap-10 px-6 py-32 text-center sm:px-8 lg:px-12">
          <div className="max-w-3xl space-y-6">
            <span className="inline-flex items-center rounded-full bg-white/10 px-4 py-2 text-sm font-medium uppercase tracking-widest text-amber-200/90">
              Safari experiences since 2007
            </span>
            <h1 className="text-4xl font-bold tracking-tight sm:text-5xl lg:text-6xl">
              Yala Safari – Wild Beauty of Sri Lanka
            </h1>
            <p className="text-lg text-gray-300 sm:text-xl">
              Experience the thrill of the wild at Yala National Park, Sri
              Lanka’s most famous wildlife destination. From majestic leopards
              to gentle elephants and colorful birds, Yala offers an
              unforgettable safari adventure surrounded by nature’s raw beauty.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link
                to="/packages"
                className="inline-flex items-center rounded-md bg-[#c4915f] px-6 py-3 text-base font-semibold text-white shadow-lg shadow-[#c4915f]/30 transition-all duration-200 hover:-translate-y-0.5 hover:bg-[#b37f4a] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#c4915f]"
              >
                Book Your Safari
              </Link>
              <Link
                to="/about"
                className="inline-flex items-center rounded-md border border-white/20 px-6 py-3 text-base font-semibold text-gray-100 transition-colors duration-200 hover:border-white hover:text-white"
              >
                Meet Our Naturalists
              </Link>
            </div>
          </div>
          <div className="grid max-w-xl gap-6 text-sm text-gray-300 sm:grid-cols-3">
            <div className="rounded-lg border border-white/5 bg-white/5 p-4 backdrop-blur">
              <p className="font-semibold text-white">Leopard Capital</p>
              <p>
                Highest leopard density globally. Track the elusive predator
                through Block 1's rocky outcrops.
              </p>
            </div>
            <div className="rounded-lg border border-white/5 bg-white/5 p-4 backdrop-blur">
              <p className="font-semibold text-white">Ancient Wilderness</p>
              <p>
                979 sq km of dry zone forest, lagoons, and Buddhist monastery
                ruins dating back centuries.
              </p>
            </div>
            <div className="rounded-lg border border-white/5 bg-white/5 p-4 backdrop-blur">
              <p className="font-semibold text-white">Wildlife Haven</p>
              <p>
                Home to 44 mammals including sloth bears, elephants, and over
                215 bird species year-round.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Packages */}
      <section className="relative py-20">
        <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />
        <div className="mx-auto max-w-7xl px-6 sm:px-8 lg:px-12">
          <div className="mx-auto max-w-3xl text-center">
            <h2 className="text-3xl font-semibold tracking-tight sm:text-4xl">
              Signature Safari Collections
            </h2>
            <p className="mt-4 text-base text-gray-300">
              Designed by resident rangers, each itinerary blends premium
              comfort with once-in-a-lifetime wildlife encounters.
            </p>
          </div>
          <div className="mt-16 grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
            {packages.map((pkg) => (
              <article
                key={pkg.name}
                className="group flex h-full flex-col rounded-2xl border border-white/10 bg-white/5 p-8 shadow-xl shadow-black/30 transition-transform duration-300 hover:-translate-y-2 hover:border-white/20 hover:bg-white/10"
              >
                <div className="flex-1 space-y-4">
                  <h3 className="text-2xl font-semibold text-white">
                    {pkg.name}
                  </h3>
                  <p className="text-sm leading-relaxed text-gray-300">
                    {pkg.description}
                  </p>
                  <div className="flex flex-col gap-2 text-sm text-gray-400">
                    <span className="inline-flex items-center gap-2 text-amber-200">
                      <span className="h-2 w-2 rounded-full bg-amber-400" />
                      {pkg.duration}
                    </span>
                    <span>{pkg.price}</span>
                  </div>
                </div>
                <div className="mt-8">
                  <Link
                    to="/packages"
                    className="flex items-center justify-center rounded-md bg-[#c4915f] px-4 py-2 text-sm font-semibold text-white shadow-lg shadow-[#c4915f]/30 transition-all duration-200 group-hover:bg-[#b37f4a]"
                  >
                    Reserve This Experience
                  </Link>
                </div>
              </article>
            ))}
          </div>
          <div className="mt-12 text-center">
            <Link
              to="/packages"
              className="inline-flex items-center rounded-md border border-white/20 px-5 py-2.5 text-sm font-semibold text-gray-100 transition-colors duration-200 hover:border-white hover:text-white"
            >
              Explore all itineraries
            </Link>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="relative border-y border-white/5 bg-black/20 py-20">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,_rgba(196,145,95,0.15),_transparent_55%)]" />
        <div className="relative mx-auto flex max-w-7xl flex-col items-center gap-16 px-6 sm:px-8 lg:flex-row lg:gap-20 lg:px-12">
          <div className="relative lg:w-1/2">
            <div className="relative overflow-hidden rounded-3xl border border-white/10 shadow-2xl shadow-black/40">
              <img
                src={safariJeep}
                alt="Safari Jeep in Yala National Park"
                className="h-full w-full object-cover transition-transform duration-500 hover:scale-[1.03]"
              />
              <div className="absolute inset-0 bg-gradient-to-tr from-black/50 via-transparent to-black/20" />
              <div className="absolute bottom-6 left-6 space-y-1">
                <h3 className="text-2xl font-semibold text-white">
                  Yala Safari Adventures
                </h3>
                <p className="text-sm text-amber-200/80">
                  Guided by experts. Driven by conservation.
                </p>
              </div>
            </div>
          </div>
          <div className="lg:w-1/2">
            <span className="text-sm font-semibold uppercase tracking-[0.3em] text-amber-200/70">
              About Yala Safari
            </span>
            <h2 className="mt-4 text-3xl font-semibold tracking-tight sm:text-4xl">
              Luxury lodging with the soul of the wilderness
            </h2>
            <p className="mt-6 text-base text-gray-300">
              Enjoy an exciting jeep safari with a professional guide, early
              morning or evening, when animals are most active. Capture
              breathtaking photos and feel the true spirit of the Sri Lankan
              wilderness.
            </p>
            <p className="mt-4 text-base text-gray-300">
              Our concierge team manages airport transfers, private charters,
              wellness treatments, and tailored excursions across Yala, Bundala,
              and Udawalawe. Every stay is supported by certified rangers and
              eco-conscious hosts who protect the ecosystems we explore.
            </p>
            <Link
              to="/about"
              className="mt-8 inline-flex items-center rounded-md bg-white/10 px-6 py-3 text-sm font-semibold text-white transition-colors duration-200 hover:bg-white/20"
            >
              Discover our story
              <svg
                className="ml-2 h-5 w-5"
                xmlns="http://www.w3.org/2000/svg"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M10.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L12.586 11H5a1 1 0 110-2h7.586l-2.293-2.293a1 1 0 010-1.414z"
                  clipRule="evenodd"
                />
              </svg>
            </Link>
          </div>
        </div>
      </section>

      {/* Reviews Section */}
      <section className="relative py-20">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_rgba(25,46,59,0.4),_transparent_65%)]" />
        <div className="relative mx-auto max-w-7xl px-6 sm:px-8 lg:px-12">
          <div className="text-center">
            <h2 className="text-3xl font-semibold tracking-tight sm:text-4xl">
              Trusted by discerning travellers worldwide
            </h2>
            <p className="mt-4 text-base text-gray-300">
              {stats ? (
                <>
                  Independently reviewed with an average rating of{" "}
                  <span className="font-semibold text-amber-300">
                    {stats.averageRating?.toFixed(1)}
                  </span>{" "}
                  from{" "}
                  <span className="font-semibold text-amber-300">
                    {stats.totalReviews}
                  </span>{" "}
                  verified reviews.
                </>
              ) : (
                "Independently reviewed on TripAdvisor and Google with a perfect 5.0 rating."
              )}
            </p>
          </div>

          {stats && (
            <div className="mt-16 grid gap-8 md:grid-cols-2">
              <div className="overflow-hidden rounded-2xl border border-white/10 bg-gradient-to-br from-[#1f2d3a] via-[#1a2732] to-[#111820] shadow-2xl">
                <div className="bg-gradient-to-r from-emerald-600/80 to-green-700/80 px-6 py-5">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="rounded-full bg-white/10 p-3">
                        <svg
                          className="h-10 w-10 text-white"
                          viewBox="0 0 24 24"
                          fill="currentColor"
                        >
                          <path d="M12.006 4.295c-2.67 0-5.338.784-7.645 2.353-.264.179-.529.357-.784.546L.674 4.295l2.034 4.403c-.784 1.05-1.307 2.25-1.577 3.524C.4 14.553.848 17.663 3.014 19.99c1.902 2.034 4.59 3.05 7.278 2.758 2.688-.291 5.128-1.668 6.617-3.738 1.488-2.07 2.034-4.687 1.49-7.15-.546-2.463-2.07-4.59-4.143-5.773-.784-.446-1.63-.79-2.51-1.036-.879-.246-1.79-.378-2.688-.378-.358 0-.714.022-1.07.067zm-.015 2.045c.335-.022.67-.022.995.011 1.307.134 2.547.658 3.56 1.507 1.013.85 1.767 2.003 2.17 3.288.402 1.285.447 2.67.134 3.977-.313 1.307-.993 2.503-1.955 3.427-1.966 1.88-4.98 2.347-7.41 1.15-2.43-1.196-3.987-3.784-3.83-6.36.156-2.577 1.99-4.88 4.511-5.675.79-.246 1.623-.358 2.456-.335zm-5.362 2.645c-.201 0-.402.034-.592.1-.38.134-.714.402-.936.758-.223.357-.313.79-.246 1.196.067.402.29.769.614 1.024.324.257.737.39 1.15.38.413-.012.804-.168 1.105-.435.302-.268.502-.636.57-1.036.067-.402-.012-.815-.223-1.15-.212-.335-.535-.592-.926-.725-.19-.067-.392-.1-.592-.1zm10.682 0c-.201 0-.402.034-.592.1-.38.134-.714.402-.937.758-.223.357-.313.79-.246 1.196.067.402.29.769.614 1.024.324.257.737.39 1.15.38.413-.012.804-.168 1.105-.435.302-.268.502-.636.57-1.036.067-.402-.012-.815-.223-1.15-.212-.335-.535-.592-.926-.725-.19-.067-.392-.1-.592-.1z" />
                        </svg>
                      </div>
                      <div>
                        <p className="text-sm uppercase tracking-[0.35em] text-white/70">
                          Customer Reviews
                        </p>
                        <p className="text-xl font-semibold text-white">
                          {stats.averageRating?.toFixed(1)}{" "}
                          {stats.averageRating >= 4.5
                            ? "Excellent"
                            : stats.averageRating >= 4.0
                              ? "Very Good"
                              : "Good"}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="space-y-6 px-6 py-6 text-gray-200">
                  <div className="space-y-2">
                    <p className="text-sm font-semibold text-white">
                      Rating Breakdown
                    </p>
                    <div className="space-y-1 text-xs">
                      <div className="flex items-center justify-between">
                        <span>5 Stars</span>
                        <span className="font-semibold">
                          {stats.fiveStars || 0}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span>4 Stars</span>
                        <span className="font-semibold">
                          {stats.fourStars || 0}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span>3 Stars</span>
                        <span className="font-semibold">
                          {stats.threeStars || 0}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span>2 Stars</span>
                        <span className="font-semibold">
                          {stats.twoStars || 0}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span>1 Star</span>
                        <span className="font-semibold">
                          {stats.oneStar || 0}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="overflow-hidden rounded-2xl border border-white/10 bg-gradient-to-br from-[#1e2935] via-[#1a2430] to-[#10161d] shadow-2xl">
                <div className="bg-gradient-to-r from-sky-600/80 to-blue-700/80 px-6 py-5">
                  <div className="flex items-center gap-3">
                    <div className="rounded-full bg-white/10 p-3">
                      <svg
                        className="h-10 w-10 text-white"
                        viewBox="0 0 24 24"
                        fill="none"
                      >
                        <path
                          d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                          fill="currentColor"
                        />
                        <path
                          d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77a6.73 6.73 0 01-10.87-3.47H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                          fill="currentColor"
                          opacity="0.7"
                        />
                      </svg>
                    </div>
                    <div>
                      <p className="text-sm uppercase tracking-[0.35em] text-white/70">
                        Verified Reviews
                      </p>
                      <p className="text-xl font-semibold text-white">
                        {stats.totalReviews} Total Reviews
                      </p>
                    </div>
                  </div>
                </div>
                <div className="space-y-6 px-6 py-6 text-gray-200">
                  <p className="text-base italic">
                    &quot;All reviews are verified from real customers who have
                    experienced our safari services. We value authentic feedback
                    and continuously improve based on guest experiences.&quot;
                  </p>
                  <div className="flex items-center justify-between text-sm text-gray-400">
                    <span>See all verified reviews</span>
                    <a
                      href="#reviews"
                      className="inline-flex items-center rounded-md bg-white/10 px-4 py-2 text-xs font-semibold text-white transition-colors duration-200 hover:bg-white/20"
                    >
                      View Reviews
                      <svg
                        className="ml-2 h-4 w-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                        />
                      </svg>
                    </a>
                  </div>
                </div>
              </div>
            </div>
          )}

          <h3 id="reviews" className="mt-20 text-center text-2xl font-semibold">
            Recent guest experiences
          </h3>
          {loading ? (
            <div className="mt-10 text-center text-gray-400">
              <p>Loading reviews...</p>
            </div>
          ) : error ? (
            <div className="mt-10 text-center text-red-400">
              <p>Unable to load reviews. Please try again later.</p>
            </div>
          ) : reviews.length === 0 ? (
            <div className="mt-10 text-center text-gray-400">
              <p>No reviews available at the moment.</p>
            </div>
          ) : (
            <div className="ui-mdc-testimonial-carousel mt-10">
              <Slider
                dots={true}
                infinite={reviews.length > 3}
                speed={500}
                slidesToShow={Math.min(3, reviews.length)}
                slidesToScroll={1}
                autoplay={true}
                autoplaySpeed={2000}
                pauseOnHover={true}
                centerMode={reviews.length > 1}
                centerPadding="0px"
                responsive={[
                  {
                    breakpoint: 1024,
                    settings: {
                      slidesToShow: Math.min(2, reviews.length),
                      slidesToScroll: 1,
                      centerMode: reviews.length > 1,
                    },
                  },
                  {
                    breakpoint: 640,
                    settings: {
                      slidesToShow: 1,
                      slidesToScroll: 1,
                      centerMode: false,
                    },
                  },
                ]}
                className="ui-mdc-carousel"
              >
                {reviews.map((review) => (
                  <div key={review._id} className="ui-mdc-carousel-slide">
                    <div className="ui-mdc-carousel-card">
                      <div className="mb-4 flex items-center justify-between">
                        <div className="flex items-center">
                          {[...Array(5)].map((_, i) => (
                            <svg
                              key={i}
                              className={`h-5 w-5 ${i < review.rating ? "text-amber-300" : "text-gray-600"}`}
                              fill="currentColor"
                              viewBox="0 0 20 20"
                            >
                              <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                            </svg>
                          ))}
                        </div>
                        {review.isVerified && (
                          <span className="rounded-full bg-emerald-500/20 px-3 py-1 text-xs font-semibold text-emerald-200">
                            Verified
                          </span>
                        )}
                      </div>
                      <blockquote className="flex-1 text-sm text-gray-200">
                        <p>&ldquo;{review.reviewMessage}&rdquo;</p>
                      </blockquote>
                      <div className="mt-6 flex items-center gap-3">
                        {review.customerPhoto ? (
                          <img
                            src={getImageUrl(review.customerPhoto)}
                            alt={review.customerName}
                            className="h-12 w-12 rounded-full object-cover"
                            onError={(e) => {
                              // Fallback to initial if image fails to load
                              e.target.style.display = "none";
                              e.target.nextSibling.style.display = "flex";
                            }}
                          />
                        ) : null}

                       
                        <div>
                          <p className="text-sm font-semibold text-white">
                            {review.customerName}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </Slider>
            </div>
          )}

          <div className="mt-12 flex flex-wrap justify-center gap-4 text-sm font-semibold">
            {/* <a
              href="/"
              // target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center rounded-md border border-white/20 px-6 py-3 text-gray-100 transition-colors duration-200 hover:border-white hover:text-white"
            >
              TripAdvisor profile
            </a> */}
            <a
              href="/"
              // target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center rounded-md border border-white/20 px-6 py-3 text-gray-100 transition-colors duration-200 hover:border-white hover:text-white"
            >
              Google reviews
            </a>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="relative border-t border-white/5 bg-gradient-to-r from-[#11171f] via-[#151d27] to-[#0d1218] py-16">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_right,_rgba(196,145,95,0.15),_transparent_55%)]" />
        <div className="relative mx-auto flex max-w-7xl flex-col items-start justify-between gap-8 px-6 sm:px-8 lg:flex-row lg:items-center lg:px-12">
          <div className="max-w-2xl">
            <p className="text-sm font-semibold uppercase tracking-[0.35em] text-amber-200/70">
              Plan ahead
            </p>
            <h2 className="mt-4 text-3xl font-semibold sm:text-4xl">
              What You’ll Experience .
              <span className="block text-lg font-normal text-gray-300">
                Yala Safari is perfect for nature lovers, photographers,
                couples, families, and adventure seekers.
              </span>
            </h2>
          </div>
          <div className="flex flex-col gap-4 sm:flex-row">
            <Link
              to="/packages"
              className="inline-flex items-center rounded-md bg-[#c4915f] px-6 py-3 text-sm font-semibold text-white shadow-lg shadow-[#c4915f]/30 transition-all duration-200 hover:-translate-y-0.5 hover:bg-[#b37f4a]"
            >
              Plan your itinerary
            </Link>
            <Link
              to="/contact"
              className="inline-flex items-center rounded-md border border-white/20 px-6 py-3 text-sm font-semibold text-gray-100 transition-colors duration-200 hover:border-white hover:text-white"
            >
              Schedule a consultation
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
}
